/* eslint-disable react/prop-types */
import {
  useContext,
  useEffect,
  useState,
  useMemo,
  forwardRef,
  memo,
} from 'react';
import ReactTooltip from 'react-tooltip';
import ReactHtmlParser from 'react-html-parser';
import shallow from 'zustand/shallow';
import MathJax from 'react-mathjax-preview';
import { v4 as uuidv4 } from 'uuid';

import useCanRefresh from './useCanRefresh';
import ExamFill from './ExamFillMinimalis';

import { safeJoinURL } from 'libs/url';
import Typography from 'components/Typography';
import useBaseURL from 'hooks/useBaseURL';
import clsx from 'libs/clsx';
import Button from 'components/Button';
import { openModalImage } from 'actions/app.action';
import AppContext from 'context/app.context';

const MATH_ML_PATTERN = /<math(.*?<\/math>)/g;

function ExamQuestionFill(
  {
    currentQuestionIndex,
    question,
    questionTotal,
    onRefresh,
    answer,
    onAnswerChange,
    // answerComponent, // Tidak memerlukan answer component, karena sudah di handle di component pertanyaan
    examNavigation,
  },
  ref
) {
  const path = useBaseURL();
  const [, dispatchApp] = useContext(AppContext);

  const [isRefreshLoading, setIsRefreshLoading] = useState(false);
  const [mathElementsTtb, setMathElementsTtb] = useState([]);

  const handleHighlightImage = (url) => {
    dispatchApp(openModalImage(url));
  };

  // @todo optimasi component yang terender. Terlalu banyak merender.
  const refresh = useCanRefresh((state) => state, shallow);
  const refreshStatus = useCanRefresh(
    (state) => ({
      status: state.list[currentQuestionIndex]?.status,
      willEnabledOn: state.list[currentQuestionIndex]?.willEnabledOn,
      enableBackByTimeout: () =>
        state.enableBackByTimeout(currentQuestionIndex),
    }),
    shallow
  );

  useEffect(() => {
    if (refresh.list.length === 0 || refresh.list.length !== questionTotal) {
      refresh.initList(questionTotal);
    }
  }, [questionTotal, refresh, refresh.list.length]);

  // Apakah pertanyaan memiliki karakter [img],
  const questionContainImg = /\[img\]/i.test(question.question);

  const isGBRContainMp3 = /\.(?:wav|mp3)$/i.test(question.image);

  // Mengubah mathML asli menjadi span.
  const sanitizedTtb = useMemo(() => {
    const additionalQuestionString = question.additionalQuestion.replace(
      /@url\//g,
      `${path.questionImage}s/`
    );

    // Mereplace mathML menjadi span.
    let index = 0;
    return additionalQuestionString.replace(
      MATH_ML_PATTERN,
      // eslint-disable-next-line no-plusplus
      () => `<span mathjax="true" index="${index++}"></span>`
    );
  }, [path.questionImage, question.additionalQuestion]);

  // Menambahkan string mathML ke dalam state ttb.
  useEffect(() => {
    setMathElementsTtb(
      question.additionalQuestion.match(MATH_ML_PATTERN) || []
    );
  }, [question.additionalQuestion]);

  // // Replace [@url] dengan questionImage path.
  // let tempQuestionString = question.question.replace(
  //   /@url\//g,
  //   `${path.questionImage}p/`
  // );

  // Replace [@url] dengan questionImage path.
  // const additionalQuestionString = ttbStringMathML.replace(
  //   /@url\//g,
  //   `${path.questionImage}s/`
  // );

  // const AnswerContainerKu = ExamFill;

  // const AnswerContainerKu = () => (
  //   <ExamFill
  //     ref={ref}
  //     question={question}
  //     answer={answer}
  //     onAnswerChange={onAnswerChange}
  //     resetAnswer={resetAnswer}
  //   />
  // );

  // const componentInput = <AnswerContainer ref={ref} question={question} answer={answer} onAnswerChange={onAnswerChange} resetAnswer={resetAnswer} />

  // Replace [@url] dengan questionImage path.
  // tempQuestionString = tempQuestionString.replace(
  //   /<@isian>/i,
  //   // ReactDOMServer.renderToString(<Input label="" />)
  //   ''
  // );

  // const questionString = tempQuestionString;
  // tempQuestionString = tempQuestionString.replace(/<([^>]+)>/i, '');
  // const partQuestions = tempQuestionString.split(/<@isian>/i);

  // const questionArabic = /[\u0600-\u06FF]/.test(question.question);
  // const questionTtbArabic = /[\u0600-\u06FF]/.test(question.additionalQuestion);

  // Refresh soal.
  const handleRefreshClick = () => {
    if (!refreshStatus.status) return;

    setIsRefreshLoading(true);
    onRefresh({ done: () => setIsRefreshLoading(false) });

    refreshStatus.enableBackByTimeout();
  };

  return (
    <>
      <div className="flex-0">{examNavigation}</div>

      {/* Heading */}
      <div className="flex flex-0 items-center justify-start gap-5 pt-10 mb-3">
        <Typography variant="subtitle1" className="font-medium">
          Pertanyaan
        </Typography>

        <div>
          <Button
            type="button"
            onClick={handleRefreshClick}
            size="sm"
            color="secondary"
            data-tip={
              refreshStatus.status
                ? 'Refresh data soal'
                : `Anda dapat mencoba lagi dalam ${refreshStatus.willEnabledOn} detik`
            }
            loading={isRefreshLoading}
            disabled={isRefreshLoading}
          >
            Refresh&nbsp;
            {!refreshStatus.status && <>({refreshStatus.willEnabledOn})</>}
          </Button>

          <ReactTooltip />
        </div>
      </div>

      <div
        className={clsx(
          'grid shrink-0 overflow-hidden',
          question.additionalQuestion ? 'grid-cols-2 gap-10' : 'grid-cols-1'
        )}
      >
        {/* Paragraf tambahan */}
        {question.additionalQuestion && sanitizedTtb && (
          <div className="overflow-y-auto h-full">
            <Typography
              as="div"
              className={clsx('font-readable select-none mt-5')}
              gutterBottom
            >
              {ReactHtmlParser(sanitizedTtb, {
                // eslint-disable-next-line consistent-return
                transform: (node) => {
                  if (
                    node.name === 'span' &&
                    node.attribs?.mathjax === 'true'
                  ) {
                    const index = parseInt(node.attribs.index, 10);
                    const mathElement = mathElementsTtb[index];

                    if (typeof mathElement === 'string') {
                      return (
                        <MathJax
                          key={uuidv4()}
                          math={String.raw`${mathElement}`}
                          style={{ display: 'inline-block' }}
                        />
                      );
                    }
                  }
                },
              })}
            </Typography>
          </div>
        )}

        <div className="overflow-y-auto h-full">
          {/* Pertanyaan */}
          <Typography
            as="div"
            className="font-readable select-none mt-5"
            gutterBottom
          >
            <ExamFill
              ref={ref}
              question={question}
              answer={answer}
              onAnswerChange={onAnswerChange}
            />
          </Typography>

          {/* Petunjuk */}
          {question.instruction && (
            <Typography
              as="div"
              className="font-readable bg-gray-300 py-6 px-4 rounded-md shadow-md select-none mt-5"
              gutterBottom
            >
              <div
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: question.instruction,
                }}
              />
            </Typography>
          )}

          {/* Gambar tambahan */}
          {question.image && !questionContainImg && !isGBRContainMp3 && (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
            <img
              className="w-auto max-w-full mt-5 block cursor-pointer"
              src={safeJoinURL(path.questionImage, question.image)}
              alt={question.image}
              onClick={() =>
                handleHighlightImage(
                  safeJoinURL(path.questionImage, question.image)
                )
              }
            />
          )}

          {/* Mp3 tambahan */}
          {question.image && !questionContainImg && isGBRContainMp3 && (
            // eslint-disable-next-line jsx-a11y/media-has-caption
            <audio
              src={safeJoinURL(path.questionImage, question.image)}
              controls
              preload="auto"
              controlsList="nofullscreen nodownload noremoteplayback noplaybackrate"
              className="mx-auto"
            />
          )}
        </div>
      </div>
    </>
  );
}

ExamQuestionFill = forwardRef(ExamQuestionFill);

export default memo(ExamQuestionFill);
